import HIIExpenseSubjectToHireDays from '@shared/hireInvoice/HIIExpenseSubjectToHireDays';
import HIIHirePeriod from '@shared/hireInvoice/HIIHirePeriod';
import {
	ExpensesSubjectToHireDaysIntervalLabels,
	InvoiceTemplates,
	PnlGroups,
} from '@shared/utils/constants';
import { toMoment } from '@shared/utils/date';
import { round } from '@shared/utils/math';
import { formatCurrency } from '../utils/currency';
import TemplateItem from '../TemplateItem';
import HireInvoiceItem from './HireInvoiceItem';

class HIIOffHireExpenseSubjectToHireDays extends HireInvoiceItem {
	static get itemType() {
		return 'offHireExpenseSubjectToHireDays';
	}

	constructor(id, isOriginal, params) {
		super(id, isOriginal, { fixtureCurrency: params.fixtureCurrency });

		this.name = params.name;
		this.amount = params.amount;
		this.interval = params.interval;
		this.calculationMethodForExpensesSubjectToHireDays =
			params.calculationMethodForExpensesSubjectToHireDays;
		this.pnlGroup = PnlGroups.OFFHIRE;
		this.itemTypeLabel = params.name;

		this._showTotal = true;
		this.addManually = false;
		this.templateSection = TemplateItem.Sections.EXPENSES_CONTRACTUAL;

		this._fields = {};
	}

	getCorrespondingHireExpenseSubjectToHireDays(invoiceItems) {
		const offHirePeriod = this.getParent(invoiceItems);

		if (offHirePeriod == null) {
			return null;
		}

		const hirePeriod = this._getCorrespondingHirePeriod(invoiceItems, offHirePeriod);

		if (hirePeriod == null) {
			return null;
		}

		const offHireCves = offHirePeriod.children.filter(
			(child) => child instanceof HIIOffHireExpenseSubjectToHireDays,
		);
		const hireCves = hirePeriod.children.filter(
			(child) => child instanceof HIIExpenseSubjectToHireDays,
		);

		const cveIndex = offHireCves.indexOf(this);

		if (cveIndex < 0 || cveIndex >= hireCves.length) {
			return null;
		}

		return hireCves[cveIndex];
	}

	_getCorrespondingHirePeriod(invoiceItems, offHirePeriod) {
		const offHireStart = toMoment(offHirePeriod.startTime);
		const offHireEnd = toMoment(offHirePeriod.endTime);

		const hirePeriods = invoiceItems.filter((item) => item instanceof HIIHirePeriod);

		const containingPeriod = hirePeriods.find((period) => {
			const hireStart = toMoment(period.from);
			const hireEnd = toMoment(period.to);

			return offHireStart.isSameOrAfter(hireStart) && offHireEnd.isSameOrBefore(hireEnd);
		});

		if (containingPeriod != null) {
			return containingPeriod;
		}

		return hirePeriods.find((period) => {
			const hireStart = toMoment(period.from);
			const hireEnd = toMoment(period.to);

			return offHireStart.isBefore(hireEnd) && offHireEnd.isAfter(hireStart);
		}) ?? null;
	}

	_getExpense(invoiceItems) {
		const expenseInstance = new HIIExpenseSubjectToHireDays(-1, this.isOriginal, {
			name: this.name,
			amount: this.amount,
			interval: this.interval,
			formulaType: this.calculationMethodForExpensesSubjectToHireDays,
		});

		const offHirePeriod = this.getParent(invoiceItems);

		if (offHirePeriod == null) {
			return {
				amount: 0,
				total: 0,
			};
		}

		const days = offHirePeriod.getDays();

		return {
			amount: expenseInstance.amount,
			total: (
				expenseInstance.calculateAmount(days) *
				(offHirePeriod.percentageForOwnersAccount / 100)
			),
		};
	}

	_calculateTotal(invoiceItems) {
		const { total } = this._getExpense(invoiceItems);

		return round(-1 * total, 2);
	}

	getDescription() {
		return `${formatCurrency(this.amount, this.fixtureCurrency)} ${ExpensesSubjectToHireDaysIntervalLabels[this.interval]}`;
	}

	static async _getClassParams(model, parentModel) {
		const voyage = await parentModel.getVoyage();
		const fixture = await voyage.getFixture();
		const tcTerms = voyage.tcTerms ??
			await voyage.getTcTerms?.() ??
			fixture.tcTerms ??
			await fixture.getTcTerms?.();
		const { childFixture } = fixture;

		return {
			name: model.name,
			amount: model.amount,
			interval: model.interval,
			calculationMethodForExpensesSubjectToHireDays:
				tcTerms?.calculationMethodForExpensesSubjectToHireDays,
			fixtureCurrency: childFixture.fixtureCurrency,
		};
	}

	async saveModel(model) {
		await super.saveModel(model);

		model.name = this.name;
		model.amount = this.amount;
		model.interval = this.interval;

		await model.save();
	}

	_getTemplateItemParams(invoiceItems) {
		const { amount } = this._getExpense(invoiceItems);

		return {
			[InvoiceTemplates.CLASSIC]: {
				columns: [this.name, formatCurrency(amount, this.fixtureCurrency)],
			},
			[InvoiceTemplates.NEO]: {
				columns: [
					` — ${this.name} (${ExpensesSubjectToHireDaysIntervalLabels[this.interval]})`,
					'',
					'1',
					`${formatCurrency(this.getTotal(invoiceItems), this.fixtureCurrency, { hideSymbol: true })}`,
				],
			},
		};
	}
}

HireInvoiceItem.addItemClass(HIIOffHireExpenseSubjectToHireDays);

export default HIIOffHireExpenseSubjectToHireDays;
